import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../services/api';

// Async thunks for flight operations
export const searchFlights = createAsyncThunk(
  'flights/search',
  async (searchParams, { rejectWithValue }) => {
    try {
      const response = await api.get('/flights/search', { params: searchParams });
      
      if (response.success) {
        return response.data;
      } else {
        return rejectWithValue(response.message);
      }
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const getFlightDetails = createAsyncThunk(
  'flights/getDetails',
  async (flightId, { rejectWithValue }) => {
    try {
      const response = await api.get(`/flights/${flightId}`);
      
      if (response.success) {
        return response.data;
      } else {
        return rejectWithValue(response.message);
      }
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const getAirports = createAsyncThunk(
  'flights/getAirports',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/utility/airports');
      
      if (response.success) {
        return response.data;
      } else {
        return rejectWithValue(response.message);
      }
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const getAircraftTypes = createAsyncThunk(
  'flights/getAircraftTypes',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/utility/aircraft-types');
      
      if (response.success) {
        return response.data;
      } else {
        return rejectWithValue(response.message);
      }
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Initial state
const initialState = {
  // Search results
  searchResults: [],
  searchLoading: false,
  searchError: null,
  
  // Flight details
  selectedFlight: null,
  flightDetailsLoading: false,
  flightDetailsError: null,
  
  // Search parameters
  searchParams: {
    departure_airport: '',
    arrival_airport: '',
    departure_date: '',
    return_date: '',
    passengers: 1,
    trip_type: 'oneway', // oneway, roundtrip
    aircraft_type: '',
  },
  
  // Utility data
  airports: [],
  aircraftTypes: [],
  airportsLoading: false,
  aircraftTypesLoading: false,
  
  // Filters
  filters: {
    priceRange: [0, 10000],
    departureTime: 'any',
    aircraftType: 'any',
    duration: 'any',
  },
  
  // Sorting
  sortBy: 'price', // price, duration, departure_time
  sortOrder: 'asc', // asc, desc
};

// Flight slice
const flightSlice = createSlice({
  name: 'flights',
  initialState,
  reducers: {
    clearSearchResults: (state) => {
      state.searchResults = [];
      state.searchError = null;
    },
    
    clearFlightDetails: (state) => {
      state.selectedFlight = null;
      state.flightDetailsError = null;
    },
    
    updateSearchParams: (state, action) => {
      state.searchParams = { ...state.searchParams, ...action.payload };
    },
    
    clearSearchParams: (state) => {
      state.searchParams = initialState.searchParams;
    },
    
    updateFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    
    clearFilters: (state) => {
      state.filters = initialState.filters;
    },
    
    setSorting: (state, action) => {
      const { sortBy, sortOrder } = action.payload;
      state.sortBy = sortBy;
      state.sortOrder = sortOrder;
    },
    
    clearSearchError: (state) => {
      state.searchError = null;
    },
    
    clearFlightDetailsError: (state) => {
      state.flightDetailsError = null;
    },
  },
  
  extraReducers: (builder) => {
    builder
      // Search Flights
      .addCase(searchFlights.pending, (state) => {
        state.searchLoading = true;
        state.searchError = null;
      })
      .addCase(searchFlights.fulfilled, (state, action) => {
        state.searchLoading = false;
        state.searchResults = action.payload.flights || [];
        state.searchError = null;
      })
      .addCase(searchFlights.rejected, (state, action) => {
        state.searchLoading = false;
        state.searchError = action.payload;
        state.searchResults = [];
      })
      
      // Get Flight Details
      .addCase(getFlightDetails.pending, (state) => {
        state.flightDetailsLoading = true;
        state.flightDetailsError = null;
      })
      .addCase(getFlightDetails.fulfilled, (state, action) => {
        state.flightDetailsLoading = false;
        state.selectedFlight = action.payload;
        state.flightDetailsError = null;
      })
      .addCase(getFlightDetails.rejected, (state, action) => {
        state.flightDetailsLoading = false;
        state.flightDetailsError = action.payload;
        state.selectedFlight = null;
      })
      
      // Get Airports
      .addCase(getAirports.pending, (state) => {
        state.airportsLoading = true;
      })
      .addCase(getAirports.fulfilled, (state, action) => {
        state.airportsLoading = false;
        state.airports = action.payload;
      })
      .addCase(getAirports.rejected, (state) => {
        state.airportsLoading = false;
      })
      
      // Get Aircraft Types
      .addCase(getAircraftTypes.pending, (state) => {
        state.aircraftTypesLoading = true;
      })
      .addCase(getAircraftTypes.fulfilled, (state, action) => {
        state.aircraftTypesLoading = false;
        state.aircraftTypes = action.payload;
      })
      .addCase(getAircraftTypes.rejected, (state) => {
        state.aircraftTypesLoading = false;
      });
  },
});

export const {
  clearSearchResults,
  clearFlightDetails,
  updateSearchParams,
  clearSearchParams,
  updateFilters,
  clearFilters,
  setSorting,
  clearSearchError,
  clearFlightDetailsError,
} = flightSlice.actions;

export default flightSlice.reducer;